import type {
  ApplicationLogger,
  LogDataType,
} from "../types/application-logger.types";
import { ApplicationLoggerOutput } from "../types/application-logger-output.types";
import { ConsoleOutputImpl } from "./console-output.impl";

export abstract class ApplicationLoggerImpl implements ApplicationLogger {
  protected outputs: ApplicationLoggerOutput[] = [];
  protected allowedLogLevels: string[] = [];
  protected isProduction: boolean;

  constructor(isProduction: boolean) {
    this.isProduction = isProduction;
    this.initializeLogger();
  }

  private initializeLogger(): void {
    try {
      const logLevelsEnv = this.getLogLevelsFromEnvironment();
      if (logLevelsEnv !== undefined && logLevelsEnv !== "") {
        const levels = logLevelsEnv
          .split(",")
          .map((level: string) => level.trim())
          .filter((level: string) => level.length > 0);
        this.setAllowedLogLevels(levels);
      } else {
        this.setAllowedLogLevels(["debug", "info", "warn", "error", "log"]);
      }

      this.addConsoleOutput();

      this.addAdditionalOutputs();
    } catch (err) {
      // Fallback to safe defaults if initialization fails
      this.handleInitializationError(err);
      this.setAllowedLogLevels(["debug", "info", "warn", "error", "log"]);
      this.setOutputs([new ConsoleOutputImpl()]);
    }
  }

  protected abstract getLogLevelsFromEnvironment(): string | undefined;

  protected addConsoleOutput(): void {
    this.addOutput(new ConsoleOutputImpl());
  }

  protected addAdditionalOutputs(): void {
    // Default implementation does nothing
    // Subclasses can override to add file logging, analytics, etc.
  }

  // eslint-disable-next-line planta-v2/unknown-only-at-boundaries -- Logger legitimately handles unknown errors from initialization
  protected handleInitializationError(error: unknown): void {
    if (!this.isProduction) {
      console.warn(
        "Logger initialization failed, using fallback configuration:",
        error,
      );
    }
  }

  public setAllowedLogLevels(levels: string[]): void {
    this.allowedLogLevels = levels;
  }

  protected addOutput(output: ApplicationLoggerOutput): void {
    this.outputs.push(output);
  }

  protected setOutputs(outputs: ApplicationLoggerOutput[]): void {
    this.outputs = outputs;
  }

  protected safeLog(
    level: string,
    operation: (output: ApplicationLoggerOutput) => void,
  ): void {
    try {
      if (!this.allowedLogLevels.includes(level)) return;
      this.outputs.forEach((output) => {
        try {
          operation(output);
        } catch (outputError) {
          // Silently fail individual output errors to prevent breaking the app

          this.handleLogError("Output failed", outputError);
        }
      });
    } catch (err) {
      // Silently fail logging to prevent breaking the application

      this.handleLogError("Logger failed", err);
    }
  }

  // eslint-disable-next-line planta-v2/unknown-only-at-boundaries -- Logger legitimately handles unknown errors from logging operations
  protected abstract handleLogError(message: string, error: unknown): void;

  public log(...params: LogDataType[]): void {
    this.safeLog("log", (output) => {
      output.log(...params);
    });
  }

  public error(
    description: string,
    location?: string,
    error?: Error,
    context?: LogDataType[],
  ): void {
    this.safeLog("error", (output) => {
      output.error(description, location, error, context);
    });
  }

  public warn(...params: LogDataType[]): void {
    this.safeLog("warn", (output) => {
      output.warn(...params);
    });
  }

  public info(...params: LogDataType[]): void {
    this.safeLog("info", (output) => {
      output.info(...params);
    });
  }

  public debug(...params: LogDataType[]): void {
    this.safeLog("debug", (output) => {
      output.debug(...params);
    });
  }
}
